import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutlined, CancelOutlined } from '@mui/icons-material';

interface DspAdvertiserStatusCellProps {
    dspAdvertiserId?: string;
    advertiserName?: string;
    isLoading?: boolean;
    dealerName: string;
}

/**
 * Amazon DSP advertiser status cell. Reads the advertiser name from the agency-level bulk map
 * (fetched once by the table via /api/agency/{id}/dspAdvertisers), so it makes no per-row network
 * call. Shows a green check when the resolved name matches the dealer name, a red X with the name
 * (and tooltip) on mismatch, and "Invalid Advertiser ID" when the id resolves to nothing.
 */
const DspAdvertiserStatusCell = ({
    dspAdvertiserId,
    advertiserName,
    isLoading,
    dealerName,
}: DspAdvertiserStatusCellProps) => {
    // No DSP advertiser configured for this dealer — leave the cell blank.
    if (!dspAdvertiserId) {
        return null;
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                    Validating...
                </Typography>
            </Box>
        );
    }

    const namesMatch = !!advertiserName && advertiserName === dealerName;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {namesMatch ? (
                <CheckCircleOutlined color="success" fontSize="small" />
            ) : (
                <Tooltip title={advertiserName && advertiserName !== dealerName ? 'Advertiser names do not match' : ''}>
                    <CancelOutlined color="error" fontSize="small" />
                </Tooltip>
            )}
            <Typography variant="body2" color="text.secondary" noWrap>
                {advertiserName ? advertiserName : 'Invalid Advertiser ID'}
            </Typography>
        </Box>
    );
};

export default DspAdvertiserStatusCell;
